import { applyLogoFallback } from '@/utils/logoFallback';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { getAuthSelector } from '@/redux/auth/auth.slice';
import { useAppSelector } from '@/redux/store.hook';
import gradeGenerator from '@/utils/gradeGenerator';
import { useState, useCallback, useEffect } from 'react';
import { Skeleton } from '@mui/material';

import { Box, Flex } from '@/layout';
import {
  BRBadge,
  SupplementalInfoBadge,
  IdentificationCheckBadge,
  ECertBadge,
  CreditReportBadge,
  AccountVerifiedBadge,
  GoGlobalBadge,
} from '@/components/UI/Badge';
import { CardTitle, CardWrapper, Row, IconBtn, Content, SectionContainer, LogoBox, PageBtnWrapper } from './style';
import categoryGenerator from '@/utils/categoryGenerator';
import { Text } from '@/components/UI/Typography/Text';
import { theme } from '@/theme/Theme';
import { Page } from '@/components/Icon';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { H1 } from '@/components/UI/Typography/Heading';
import useResize from '@/hooks/useResize';
import useCurrentLang from '@/hooks/useCurrentLang';
import Pagination from '@mui/material/Pagination';

interface ListProps {
  showData: boolean;
  data?: MemberProfile[];
  recommendData?: MemberProfile[];
  totalData: number;
  perPage: number;
  page: number;
  lastPage: number;
  total: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  type?: 'YellowPage' | 'LawFirmsList';
  currentCategory: string;
}

interface Verification {
  person: boolean;
  supplemental: boolean;
  identification: boolean;
  eCert: boolean;
  creditReport: boolean;
  accountVerified: boolean;
  goGlobal: boolean;
}

interface ImageLoadingState {
  [key: string]: boolean;
}

const List = ({
  showData,
  data = [],
  recommendData = [],
  totalData,
  page,
  perPage,
  setPage,
  lastPage,
  total,
  type = 'YellowPage',
  currentCategory,
}: ListProps) => {
  // @ts-ignore
  const { t } = useTranslation();

  const location = useLocation();
  const navigate = useNavigate();
  const currentLang = useCurrentLang();
  const { userData } = useAppSelector(getAuthSelector);
  const [searchParams, setSearchParams] = useSearchParams();
  const { isTablet, isMobile } = useResize();

  const [loadedImages, setLoadedImages] = useState<ImageLoadingState>({});

  const handleImageLoad = useCallback((id: number) => {
    setLoadedImages((prev) => ({
      ...prev,
      [id.toString()]: true,
    }));
  }, []);

  const pageHandler = (event: React.ChangeEvent<unknown>, value: number) => {
    console.log(page, value);
    setPage(value);
    searchParams.set('page', value.toString());
    searchParams.set('per_page', perPage.toString());
    setSearchParams(searchParams);
    window.scrollTo({
      top: 600,
      behavior: 'smooth',
    });
  };

  const badgeGenerater = (badgeAry: Verification) => {
    const badges: React.ReactNode[] = [];
    // badgeAry.creditReport && badges.push(<CreditReportBadge width={24} align={'center'} />);
    badgeAry.eCert && badges.push(<ECertBadge width={24} align={'center'} />);
    badgeAry.identification && badges.push(<IdentificationCheckBadge width={24} align={'center'} />);
    badgeAry.person && badges.push(<BRBadge width={24} align={'center'} />);
    badgeAry.supplemental && badges.push(<SupplementalInfoBadge width={24} align={'center'} />);

    return badges;
  };

  const goToChat = (user_id: number) => navigate('/chat/start/' + user_id);

  const CustomTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      width: 'unset',
      maxWidth: 'unset',
      fontSize: theme.fontSizes.base,
      whiteSpace: 'nowrap',
    },
  }));

  const checkWebsiteLink = (link: string) => {
    if (link.includes('http://') || link.includes('https://')) {
      return link;
    } else {
      return 'http://' + link;
    }
  };

  // 確認是否顯示徽章, 此處為陣列mapping用, 單純回傳true/false即可
  const checkShowVerifiedBadge = (data: MemberProfile) => {
    // 有子母公司的情況需子公司與母公司皆有驗證才將驗證badge顯示
    if (data.verification.accountVerified && data.childCompanies) {
      const childVerified = data.childCompanies.every((item) => item.verification.accountVerified);
      if (childVerified) {
        return true;
      }
      return false;
    }

    // 梅子公司的情況只需母公司有驗證就將驗證badge顯示
    if (data.verification.accountVerified) {
      return true;
    }
    return false;
  };

  // 每次切換頁面或資料時重置 loadedImages
  useEffect(() => {
    if (data && data.length > 0) {
      const reset: ImageLoadingState = {};
      data.forEach((item) => {
        reset[item.id.toString()] = false;
      });
      setLoadedImages(reset);
    }
  }, [data, page]);

  return (
    <SectionContainer isMobile={isMobile}>
      {type !== 'YellowPage' && <H1>{currentCategory}</H1>}
      {data.length > 0 && showData && (
        <Flex marginBottom={15} justifyContent={'space-between'}>
          <Text color={theme.colors.gray300}>
            {t('buttons.showing')} {(page - 1) * perPage + 1} {t('buttons.to')}{' '}
            {totalData - page * perPage < 0 ? totalData : page * perPage} {t('buttons.of')} {totalData}{' '}
            {t('buttons.records')}
          </Text>
          <Pagination
            size={isMobile ? 'medium' : 'large'}
            count={Math.ceil(total / 10)}
            page={page}
            onChange={pageHandler}
            variant="outlined"
            shape="rounded"
          />
        </Flex>
      )}
      {showData &&
        data.map((item, index) => (
          <CardWrapper isMobile={isMobile} key={'company' + index}>
            <Flex flexDirection="column" alignItems="center" gridGap={10}>
              <LogoBox isMobile={isMobile} isTablet={isTablet} show1hLegal={item.go_global_verified === 1 || item.acc_category !== null && item.acc_category.replace(/\s+/g, '').toLowerCase().includes('1-hourfreelegal'.toLowerCase())}>
                {!loadedImages[item.id.toString()] && (
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height="100%"
                    animation="wave"
                    style={{
                      backgroundColor: 'rgba(0, 0, 0, 0.1)',
                      borderRadius: '4px',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                    }}
                  />
                )}
                <img
                  height={'100%'}
                  width={'100%'}
                  src={item.acc_logo ?? '/images/default_company_logo.jpg'}
                  style={{
                    objectFit: 'contain',
                    opacity: loadedImages[item.id.toString()] ? 1 : 0,
                    transition: 'opacity 0.3s ease',
                  }}
                  alt={item.acc_coname || 'Company logo'}
                  onLoad={() => handleImageLoad(item.id)}
                  onError={(event) => { applyLogoFallback(event.currentTarget); handleImageLoad(item.id); }}
                />
              </LogoBox>

              {
                item.go_global_verified === 1 && item.acc_category !== null && !item.acc_category.replace(/\s+/g, '').toLowerCase().includes('1-hourfreelegal'.toLowerCase()) && (
                  <Flex
                    justifyContent={'center'}
                    alignItems={'center'}
                    gridGap={10}
                    borderRadius={'10px'}
                    padding={'5px'}
                    style={{
                      boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)',
                      maxWidth: '210px',
                      width: '100%'
                    }}>
                    <GoGlobalBadge width={80} align={'center'} tier={item.go_global_badge_tier} />
                  </Flex>
                )
              }

              {/* 僅有一小時法律諮詢 */}
              {
                item.go_global_verified === 0 && item.acc_category !== null && item.acc_category.replace(/\s+/g, '').toLowerCase().includes('1-hourfreelegal'.toLowerCase()) && (
                  <Flex
                    justifyContent={'center'}
                    alignItems={'center'}
                    gridGap={10}
                    borderRadius={'10px'}
                    padding={'5px'}
                    style={{
                      boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)',
                      maxWidth: '210px',
                      width: '100%'
                    }}>
                    <Box flex={1}>
                      <img
                        style={{ width: '100%' }}
                        src="/images/yellowpage/1hLegalConsultation.png"
                        alt="1 Hour Legal Consultation"
                      />
                    </Box>
                  </Flex>
                )
              }

              {/* 兩種都有的情況 */}
              {
                item.go_global_verified === 1 && (item.acc_category !== null && item.acc_category.replace(/\s+/g, '').toLowerCase().includes('1-hourfreelegal'.toLowerCase())) && (
                  <Flex
                    justifyContent={'center'}
                    alignItems={'center'}
                    gridGap={10}
                    borderRadius={'10px'}
                    padding={'5px'}
                    style={{
                      boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)',
                      maxWidth: '210px',
                      width: '100%'
                    }}>
                    <GoGlobalBadge width={40} align={'center'} tier={item.go_global_badge_tier} />
                    <Box flex={1}>
                      <img
                        style={{ width: '100%' }}
                        src="/images/yellowpage/1hLegalConsultation.png"
                        alt="1 Hour Legal Consultation"
                      />
                    </Box>
                  </Flex>
                )
              }
            </Flex>
            <Content isMobile={isMobile}>
              <Flex justifyContent="space-between" alignItems="flex-start">
                <Box flex={1} marginBottom={'20px'}>
                  <Flex gridGap={'5px'} alignItems={'center'}>
                    <CustomTooltip title={item.acc_coname} placement="top">
                      <CardTitle isMobile={isMobile} isTablet={isTablet}>{item.acc_coname}</CardTitle>
                    </CustomTooltip>
                    {checkShowVerifiedBadge(item) && (
                      <Flex>
                        <AccountVerifiedBadge width={24} align={'center'} />
                      </Flex>
                    )}
                  </Flex>
                </Box>
                {!isMobile && (
                  <Flex gridGap={10}>
                    {item.id !== userData.id ? (
                      <IconBtn onClick={() => goToChat(item.id)}>
                        <img src="/images/yellowpage/chat.svg" alt="" />
                      </IconBtn>
                    ) : (
                      <IconBtn className="disable">
                        <img src="/images/yellowpage/chat.svg" alt="" />
                      </IconBtn>
                    )}
                    {item.acc_website && (
                      <IconBtn>
                        <a href={checkWebsiteLink(item.acc_website)} target="_blank" rel="noreferrer">
                          <img src="/images/yellowpage/global.svg" alt="" />
                        </a>
                      </IconBtn>
                    )}
                  </Flex>
                )}
              </Flex>

              <Flex gridGap={'30px'} flexDirection={isTablet ? 'column' : 'row'} justifyContent={'space-between'} alignItems={isTablet ? 'center' : 'flex-start'}>
                <Flex flexDirection={'column'} gridGap={'12px'} width={isTablet ? '100%' : '50%'}>
                  <span>{badgeGenerater(item.verification)}</span>

                  <p>
                    {item?.acc_desc ? item.acc_desc.slice(0, 60) + '...' : item?.acc_desc}
                    <Link
                      to={`/${currentLang}/companyDetail/${item.id}`}
                      state={{ pathname: location.pathname, search: location.search }}
                      style={{ color: theme.colors.secondary500, textDecoration: 'underline', marginLeft: 10 }}
                    >
                      Read more
                    </Link>
                  </p>

                  <div className="keyword">{categoryGenerator(item.acc_category, item.acc_prodService)}</div>
                </Flex>
                <Box width={isTablet ? '100%' : '50%'}>
                  {item?.acc_tel && (
                    <Row>
                      <div className="col">
                        <div className="icon">
                          <img width={24} src="/images/yellowpage/search/tel.svg" alt="" />
                        </div>
                        <div className="info">
                          ({item?.acc_countryCode}) {item?.acc_tel}
                        </div>
                      </div>
                    </Row>
                  )}

                  {item.com_email && (
                    <Row>
                      <div className="col">
                        <div className="icon">
                          <img width={24} src="/images/yellowpage/search/mail.svg" alt="" />
                        </div>
                        <div className="info">{item.com_email}</div>
                      </div>
                    </Row>
                  )}

                  {item?.acc_address && (
                    <Row style={{ marginBottom: 0 }}>
                      <div className="col">
                        <div className="icon">
                          <img width={24} src="/images/yellowpage/search/address.svg" alt="" />
                        </div>
                        <div className="info">{item.acc_address?.split('@@_@@').join(' ')}</div>
                      </div>
                    </Row>
                  )}
                </Box>
              </Flex>

              {isMobile && (
                <Flex gridGap={10} marginTop={15}>
                  {item.id !== userData.id ? (
                    <IconBtn onClick={() => goToChat(item.id)}>
                      <img src="/images/yellowpage/chat.svg" alt="" />
                    </IconBtn>
                  ) : (
                    <IconBtn className="disable">
                      <img src="/images/yellowpage/chat.svg" alt="" />
                    </IconBtn>
                  )}
                  {item.acc_website && (
                    <IconBtn>
                      <a href={checkWebsiteLink(item.acc_website)} target="_blank" rel="noreferrer">
                        <img src="/images/yellowpage/global.svg" alt="" />
                      </a>
                    </IconBtn>
                  )}
                </Flex>
              )}
            </Content>
          </CardWrapper>
        ))
      }

      {
        showData && data.length === 0 && (
          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <Text>No result found</Text>
          </div>
        )
      }
      {
        data.length > 0 && showData && (
          <Flex marginBottom={15} justifyContent={'space-between'}>
            <Text color={theme.colors.gray300}>
              {t('buttons.showing')} {(page - 1) * perPage + 1} {t('buttons.to')}{' '}
              {totalData - page * perPage < 0 ? totalData : page * perPage} {t('buttons.of')} {totalData}{' '}
              {t('buttons.records')}
            </Text>
            <Pagination
              size={isMobile ? 'medium' : 'large'}
              count={Math.ceil(total / 10)}
              page={page}
              onChange={pageHandler}
              variant="outlined"
              shape="rounded"
            />
          </Flex>
        )
      }
      {
        type === 'LawFirmsList' && (
          <Text>
            {t('pages.yellowPage.CNSearchBar.disclaimer.title')}: <br />
            {t('pages.yellowPage.CNSearchBar.disclaimer.content')}
          </Text>
        )
      }
    </SectionContainer >
  );
};

export default List;
